import { GateResponseDto } from '@planning/app/api'
import { GateItemStore } from '@planning/rt-stores/gate/GateItemStore'
import { action, computed, makeObservable, observable, reaction } from 'mobx'

const STORAGE_KEY_PREFIX = 'planning.gateControl.lastUsedGateId'

/**
 * Session-scoped binding of the clerk's active gate. The binding lives in memory for the
 * app session (survives navigation, cleared on logout). The last-used gate id is persisted
 * client-side in localStorage purely to pre-highlight the picker — it never auto-binds.
 *
 * The available gates and their real-time updates come from {@link GateItemStore}; if the
 * bound gate leaves the active set (deactivated/deleted), the binding is invalidated.
 */
export class GateSessionStore {
  boundGate: GateResponseDto | null = null
  isPickerOpen = false
  storageScope = 'global'

  constructor(private gateItemStore: GateItemStore) {
    makeObservable(this, {
      boundGate: observable,
      isPickerOpen: observable,
      storageScope: observable,
      availableGates: computed,
      hasActiveGates: computed,
      isGateBound: computed,
      lastUsedGate: computed,
      openPicker: action,
      closePicker: action,
      confirm: action,
      clear: action,
      setStorageScope: action,
    })

    // Invalidate a stale binding the moment its gate leaves the active set.
    reaction(
      () => this.boundGate && this.availableGates.some(g => g.id === this.boundGate!.id),
      stillActive => {
        if (this.boundGate && !stillActive) this.boundGate = null
      },
    )
  }

  get availableGates(): GateResponseDto[] {
    return this.gateItemStore.activeGates
  }

  get hasActiveGates(): boolean {
    return this.availableGates.length > 0
  }

  get isGateBound(): boolean {
    return this.boundGate !== null
  }

  get lastUsedGate(): GateResponseDto | undefined {
    const lastUsedId = this.readLastUsedGateId()
    if (!lastUsedId) return undefined
    return this.availableGates.find(g => g.id === lastUsedId)
  }

  fetch = async () => {
    await this.gateItemStore.fetch()
  }

  setStorageScope = (scope: string) => {
    this.storageScope = scope || 'global'
  }

  openPicker = () => {
    this.isPickerOpen = true
  }

  closePicker = () => {
    this.isPickerOpen = false
  }

  // Explicit confirm — the only path that binds a gate.
  confirm = (gate: GateResponseDto) => {
    this.boundGate = gate
    this.writeLastUsedGateId(gate.id)
    this.isPickerOpen = false
  }

  // Session reset on logout.
  clear = () => {
    this.boundGate = null
    this.isPickerOpen = false
  }

  private get storageKey() {
    return `${STORAGE_KEY_PREFIX}.${this.storageScope}`
  }

  private readLastUsedGateId(): string | null {
    try {
      return localStorage.getItem(this.storageKey)
    } catch {
      return null
    }
  }

  private writeLastUsedGateId(gateId: string) {
    try {
      localStorage.setItem(this.storageKey, gateId)
    } catch {
      // Ignore storage failures — last-used is a non-critical hint.
    }
  }
}
